import { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Toast, ToastType } from '@/components/ui/Toast'
import { ListPageSkeleton } from '@/components/ui'
import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { WhatsAppInfoDialog } from './components/WhatsAppInfoDialog'
import { MercadoLivreInfoDialog } from './components/MercadoLivreInfoDialog'
import { MarketplaceIntegrationLogo } from './components/MarketplaceIntegrationLogo'
import { apiService } from '../../services/api'

interface Integration {
  id: string
  name: string
  description: string
  icon: 'facebook' | 'mercadolivre' | 'olx' | 'whatsapp'
  connected: boolean
  helpText: string
  setupSteps: string[]
  comingSoon?: boolean
}

export function MarketplacePage() {
  const { t } = useTranslation()
  const baseIntegrations = useMemo<Integration[]>(
    () => [
      {
        id: 'whatsapp',
        name: t('marketplace.integrations.whatsapp.name'),
        description: t('marketplace.integrations.whatsapp.description'),
        icon: 'whatsapp',
        connected: false,
        helpText: t('marketplace.integrations.whatsapp.helpText'),
        setupSteps: [],
      },
      {
        id: 'meta',
        name: t('marketplace.integrations.meta.name'),
        description: t('marketplace.integrations.meta.description'),
        icon: 'facebook',
        connected: false,
        helpText: t('marketplace.integrations.meta.helpText'),
        setupSteps: [],
        comingSoon: true,
      },
      {
        id: 'mercadolivre',
        name: t('marketplace.integrations.mercadolivre.name'),
        description: t('marketplace.integrations.mercadolivre.description'),
        icon: 'mercadolivre',
        connected: false,
        helpText: t('marketplace.integrations.mercadolivre.helpText'),
        setupSteps: [],
      },
      {
        id: 'olx',
        name: t('marketplace.integrations.olx.name'),
        description: t('marketplace.integrations.olx.description'),
        icon: 'olx',
        connected: false,
        helpText: t('marketplace.integrations.olx.helpText'),
        setupSteps: [],
        comingSoon: true,
      },
    ],
    [t],
  )
  const [integrations, setIntegrations] = useState<Integration[]>(baseIntegrations)

  const [connecting, setConnecting] = useState<string | null>(null)
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false)
  const [mercadoLivreDialogOpen, setMercadoLivreDialogOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [callbackLoading, setCallbackLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  const loadMarketplaceStatus = useCallback(async () => {
    try {
      const status = await apiService.getMarketplaceStatus()
      setIntegrations(prev => prev.map(item => {
        const normalizeId = (id: string) => id.toLowerCase().replace(/_/g, '')

        const integration = status.integrations.find(int =>
          normalizeId(int.platform) === normalizeId(item.id)
        )

        if (integration) {
          return { ...item, connected: integration.connected && integration.isActive }
        }

        return item
      }))
    } catch (err: any) {
      setIntegrations(prev => prev.map(item => ({ ...item, connected: false })))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMarketplaceStatus()
  }, [loadMarketplaceStatus])

  useEffect(() => {
    setIntegrations(prev =>
      baseIntegrations.map(baseItem => {
        const existing = prev.find(item => item.id === baseItem.id)
        return { ...baseItem, connected: existing?.connected ?? false }
      }),
    )
  }, [baseIntegrations])

  const handleConnect = async (id: string) => {
    if (id === 'meta' || id === 'olx') {
      return
    }

    // The backend OAuth callback finishes the flow with a full page redirect
    // back to /marketplace?oauth=success|error (see the effect below) — so
    // this navigates the current tab to the provider rather than opening a
    // popup. A popup would land the *popup* on /marketplace, not this tab,
    // leaving this page never finding out the connection succeeded.
    if (id === 'whatsapp') {
      try {
        setConnecting(id)
        const { url: oauthUrl } = await apiService.getMetaOAuthUrl()
        window.location.href = oauthUrl
      } catch (err: any) {
        setToast({
          message: `${t('marketplace.errors.connect')}: ${err.message || t('marketplace.errors.unknown')}`,
          type: 'error'
        })
        setConnecting(null)
      }
      return
    }

    if (id === 'mercadolivre') {
      try {
        setConnecting(id)
        const { url: oauthUrl } = await apiService.getMercadoLivreOAuthUrl()
        window.location.href = oauthUrl
      } catch (err: any) {
        setToast({
          message: `${t('marketplace.errors.connect')}: ${err.message || t('marketplace.errors.unknown')}`,
          type: 'error'
        })
        setConnecting(null)
      }
      return
    }
  }

  const handleDisconnect = async (id: string) => {
    if (id === 'meta' || id === 'olx') {
      return
    }

    if (id === 'whatsapp') {
      setWhatsappDialogOpen(true)
      return
    }

    if (id === 'mercadolivre') {
      setMercadoLivreDialogOpen(true)
      return
    }
  }

  const handleWhatsAppSuccess = async () => {
    await loadMarketplaceStatus()
  }

  const handleMercadoLivreSuccess = () => {
    loadMarketplaceStatus()
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthStatus = params.get('oauth')
    const message = params.get('message')
    const platform = params.get('platform')

    if (oauthStatus === 'success') {
      setCallbackLoading(true)

      const successMessage = platform === 'mercadolivre'
        ? t('marketplace.integrations.mercadolivre.oauthConnected')
        : t('whatsapp.success.connected')

      setToast({ message: successMessage, type: 'success' })

      setTimeout(() => {
        loadMarketplaceStatus().finally(() => {
          setCallbackLoading(false)
          window.history.replaceState({}, '', '/marketplace')
        })
      }, 1500)
    } else if (oauthStatus === 'error') {
      setToast({
        message: `${t('marketplace.errors.connect')}: ${decodeURIComponent(message || t('marketplace.errors.unknown'))}`,
        type: 'error'
      })
      window.history.replaceState({}, '', '/marketplace')
    }
  }, [t, loadMarketplaceStatus])

  if (loading || callbackLoading) {
    return <ListPageSkeleton variant="cards" rows={4} showFilters={false} />
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('marketplace.title')}</h1>
          <p className="text-muted-foreground">{t('marketplace.subtitle')}</p>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => (
          <Card
            key={integration.id}
            className={`flex flex-col ${integration.comingSoon ? 'opacity-95 border-dashed border-border' : ''}`}
          >
            <CardHeader className="flex flex-col items-center space-y-0 pb-2 pt-6 text-center">
              <div className="mb-5 flex w-full max-w-[min(16.5rem,90vw)] flex-col items-center sm:max-w-[16.5rem]">
                <div className="flex aspect-square w-full items-center justify-center rounded-2xl border border-border/60 bg-muted/80 p-3 shadow-sm sm:p-5">
                  <MarketplaceIntegrationLogo
                    iconKey={integration.icon}
                    alt={integration.name}
                    size="xl"
                    className="max-h-full max-w-full"
                  />
                </div>
                <div className="mt-5 w-full">
                  <CardTitle className="text-lg">{integration.name}</CardTitle>
                  {integration.comingSoon ? (
                    <Badge variant="info" className="mt-2 inline-flex">
                      {t('marketplace.comingSoon')}
                    </Badge>
                  ) : (
                    <Badge variant={integration.connected ? 'primary' : 'gray'} className="mt-2 inline-flex">
                      {integration.connected ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> {t('marketplace.connected')}</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> {t('marketplace.notConnected')}</>
                      )}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <CardDescription className="mb-4">
                {integration.description}
              </CardDescription>

              <div className="mt-auto space-y-3">
                {integration.comingSoon ? (
                  <>
                    <div className="p-3 bg-muted rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">{t('marketplace.comingSoonHint')}</p>
                    </div>
                    <Button type="button" className="flex-1" disabled>
                      {t('marketplace.comingSoon')}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-1">{t('marketplace.howToConnect')}</p>
                      <p className="text-xs text-muted-foreground">{integration.helpText}</p>
                    </div>

                    <div className="flex gap-2">
                      {integration.connected ? (
                        <>
                          {integration.id === 'whatsapp' ? (
                            <>
                              <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setWhatsappDialogOpen(true)}
                                disabled={loading || callbackLoading}
                              >
                                {loading || callbackLoading ? (
                                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('actions.loading')}</>
                                ) : (
                                  t('marketplace.viewDetails')
                                )}
                              </Button>
                              <Button
                                className="flex-1"
                                onClick={() => { window.location.href = '/whatsapp/templates' }}
                                disabled={loading || callbackLoading}
                              >
                                {t('marketplace.templates')}
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="destructive"
                              className="flex-1"
                              onClick={() => void handleDisconnect(integration.id)}
                              disabled={connecting === integration.id || loading || callbackLoading}
                            >
                              {connecting === integration.id ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('marketplace.disconnecting')}</>
                              ) : loading || callbackLoading ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('actions.loading')}</>
                              ) : (
                                t('marketplace.disconnect')
                              )}
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          className="flex-1"
                          onClick={() => void handleConnect(integration.id)}
                          disabled={connecting === integration.id || loading || callbackLoading}
                        >
                          {connecting === integration.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('marketplace.connecting')}</>
                          ) : loading || callbackLoading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('actions.loading')}</>
                          ) : (
                            t('marketplace.connect')
                          )}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <WhatsAppInfoDialog
        isOpen={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        onSuccess={handleWhatsAppSuccess}
      />

      <MercadoLivreInfoDialog
        isOpen={mercadoLivreDialogOpen}
        onClose={() => setMercadoLivreDialogOpen(false)}
        onSuccess={handleMercadoLivreSuccess}
      />
    </div>
    </>
  )
}
